import React, { useState, useRef, useEffect } from 'react';
import { AudioVisualizer } from 'react-audio-visualize';
import { useDispatch } from 'react-redux';
import { globalAudioActions } from '../components/GlobalAudioPlayer/slice';
import { getAudioDuration } from './audioDurationCache';
import { useIsVisible } from 'app/hooks/useIsVisible';
import { useSelector } from 'react-redux';
import { selectProgress } from './GlobalAudioPlayer/slice/selectors';
import { styled } from 'styled-components';

interface Props {
    url: string;
    currentTime: number;
    onPlay: () => {};
}

// Create a blob cache
const blobCache: Record<string, Blob> = {};

// Function to get blob from cache or fetch it
const getBlob = async (url: string): Promise<Blob> => {
    // Return from cache if available
    if (blobCache[url]) {
        return blobCache[url];
    }
    
    // Otherwise fetch and cache
    const response = await fetch(url);
    const blob = await response.blob();
    blobCache[url] = blob;
    return blob;
};

export const AudioUrlWaveformVisualizer = (props: Props) => {
    const { url, currentTime, onPlay } = props;
    const dispatch = useDispatch();
    const [blob, setBlob] = useState<Blob>();
    const [duration, setDuration] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const loadingRef = useRef(false);
    const isVisible = useIsVisible(containerRef);
    const globalAudioPlayerProgress = useSelector(selectProgress);
    const [containerWidth, setContainerWidth] = useState(200);

    // Add resize observer to update width when container size changes
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        let mounted = true;

        const loadAudio = async () => {
            if (loadingRef.current) return;
            loadingRef.current = true;

            try {
                // Get duration first (will be cached)
                const audioDuration = await getAudioDuration(url);
                if (!mounted) return;
                setDuration(audioDuration);

                // Get blob from cache or fetch it
                const cachedBlob = await getBlob(url);
                if (!mounted) return;
                setBlob(cachedBlob);
            } finally {
                loadingRef.current = false;
            }
        };

        if (isVisible) {
            loadAudio();
        }

        return () => {
            mounted = false;
        };
    }, [url, isVisible]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current || !duration) return;
        
        const rect = containerRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const progress = (clickX / rect.width) * duration;
        dispatch(globalAudioActions.setProgress(progress));
        onPlay();
    };

    return (
        <AudioWaveformContainer 
            ref={containerRef}
            onClick={handleClick}
            onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                e.currentTarget.style.setProperty('--hover-x', `${x}px`);
            }}
            style={{
                cursor: 'pointer',
                height: '18px',
                width: '100%', 
                position: 'relative',
                marginTop: '6px',
            }}
        >
            {isVisible && blob && containerWidth > 0 && (
                <AudioVisualizer
                    currentTime={currentTime}
                    blob={blob}
                    width={containerWidth}
                    height={18}
                    barWidth={2}
                    gap={2}
                    barColor={'rgba(245, 225, 255, 0.34)'}
                    barPlayedColor='#FF74E9'
                    style={{ position: 'absolute', top: 0, left: 0 }}
                />
            )}
        </AudioWaveformContainer>
    );
};

// Add a method to preload blobs for better performance
AudioUrlWaveformVisualizer.preloadBlob = async (url: string): Promise<void> => {
    if (!blobCache[url]) {
        try {
            await getBlob(url);
        } catch (error) {
            console.error('Failed to preload blob:', error);
        }
    }
};

// Add a method to clear the cache if needed
AudioUrlWaveformVisualizer.clearCache = (): void => {
    Object.keys(blobCache).forEach(key => {
        delete blobCache[key];
    });
};

const AudioWaveformContainer = styled.div`
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: var(--hover-x);
    width: 1px;
    height: 100%;
    background-color: #FF74E9;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover::before {
    opacity: 1;
  }
`